import React, { useState } from 'react';
import { useMatchStore } from '../../stores/matchStore';
import { useQueueStore } from '../../stores/queueStore';
import { Pairing } from '../../types/match';
import { ArrowPathIcon, PlayIcon, UserGroupIcon } from '@heroicons/react/24/outline';

interface PairingDisplayProps {
  className?: string;
  onMatchStart?: () => void;
}

const PairingDisplay: React.FC<PairingDisplayProps> = ({ 
  className = '',
  onMatchStart 
}) => {
  const { 
    currentPairing, 
    isLoading, 
    error,
    generatePairing,
    generateMultiplePairings,
    generateBalancedPairing,
    clearCurrentPairing,
    clearError
  } = useMatchStore();

  const { currentPlayers, canStartMatch } = useQueueStore();

  const [pairingOptions, setPairingOptions] = useState<Pairing[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerateRandomPairing = () => {
    if (!canStartMatch()) return;
    
    clearError();
    
    // 添加调试信息
    console.log('当前参赛者数据:', currentPlayers);
    console.log('每个玩家的isActive状态:', currentPlayers.map(p => ({ name: p.name, isActive: p.isActive })));
    
    generatePairing(currentPlayers);
  };

  const handleGenerateMultiplePairings = () => {
    if (!canStartMatch()) return;
    
    setIsGenerating(true);
    clearError();
    
    try {
      const options = generateMultiplePairings(currentPlayers, 3);
      setPairingOptions(options);
      setShowOptions(true);
    } catch (err) {
      console.error('生成多个配对失败:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateBalancedPairing = async () => {
    if (!canStartMatch()) return;
    
    setIsGenerating(true);
    clearError();
    
    try {
      await generateBalancedPairing(currentPlayers);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSelectPairing = (pairing: Pairing) => {
    // 手动设置选中的配对
    useMatchStore.setState({ currentPairing: pairing });
    setShowOptions(false);
    setPairingOptions([]);
  };

  const handleStartMatch = () => {
    console.log('=== 开始比赛按钮被点击 ===');
    console.log('当前配对:', currentPairing);
    
    if (!currentPairing) {
      console.error('错误：没有当前配对，无法开始比赛');
      return;
    }
    
    if (!onMatchStart) {
      console.error('错误：onMatchStart 回调函数未定义');
      return;
    }
    
    console.log('准备调用 onMatchStart 回调...');
    try {
      onMatchStart();
      console.log('onMatchStart 回调已成功调用');
    } catch (error) {
      console.error('调用 onMatchStart 时发生错误:', error);
    }
  };

  if (!canStartMatch()) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="text-center text-gray-500">
          <UserGroupIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-lg mb-2">无法生成配对</p>
          <p className="text-sm">需要至少4个参赛者才能生成配对</p>
          <p className="text-xs text-gray-400 mt-2">
            当前参赛者: {currentPlayers.length}/4
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
      <h2 className="text-xl font-bold text-gray-800 mb-6">比赛配对</h2>

      {/* 错误提示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={clearError}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 当前参赛者 */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-700 mb-3">当前参赛者</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {currentPlayers.map((player) => (
            <div
              key={player.id}
              className="bg-white border border-gray-200 rounded-lg p-2 text-center"
            >
              <div className="font-semibold text-gray-800">{player.name}</div>
              <div className="text-xs text-gray-500">#{player.number}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 配对生成按钮 */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={handleGenerateRandomPairing}
          disabled={isLoading || isGenerating}
          className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <ArrowPathIcon className="w-5 h-5" />
          <span>随机配对</span>
        </button>

        <button
          onClick={handleGenerateMultiplePairings}
          disabled={isLoading || isGenerating}
          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <UserGroupIcon className="w-5 h-5" />
          <span>多个选项</span>
        </button>

        <button
          onClick={handleGenerateBalancedPairing}
          disabled={isLoading || isGenerating}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <ArrowPathIcon className={`w-5 h-5 ${isGenerating ? 'animate-spin' : ''}`} />
          <span>平衡配对</span>
        </button>
      </div>

      {/* 配对选项 */}
      {showOptions && pairingOptions.length > 0 && (
        <div className="mb-6">
          <h3 className="font-semibold text-gray-700 mb-4">选择配对方案</h3>
          <div className="space-y-3">
            {pairingOptions.map((pairing, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => handleSelectPairing(pairing)}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-700">
                    方案 {index + 1}
                  </div>
                  <button className="text-blue-600 hover:text-blue-800 text-sm">
                    选择此方案
                  </button>
                </div>
                <PairingPreview pairing={pairing} />
              </div>
            ))}
          </div>
          
          <button
            onClick={() => {
              setShowOptions(false);
              setPairingOptions([]);
            }}
            className="mt-3 text-gray-600 hover:text-gray-800 text-sm"
          >
            取消选择
          </button>
        </div>
      )}

      {/* 当前配对显示 */}
      {currentPairing && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-700">当前配对</h3>
            <button
              onClick={clearCurrentPairing}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              重新配对
            </button>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6">
            <PairingPreview pairing={currentPairing} showVs={true} />
          </div>

          {/* 开始比赛按钮 */}
          <div className="mt-6 text-center">
            <button
              onClick={handleStartMatch}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition-colors flex items-center justify-center space-x-2 mx-auto"
            >
              <PlayIcon className="w-6 h-6" />
              <span>开始比赛</span>
            </button>
          </div>
        </div>
      )}

      {/* 操作提示 */}
      <div className="text-xs text-gray-500">
        <div className="space-y-1">
          <div>• 随机配对：完全随机分配搭档</div>
          <div>• 多个选项：生成3个不同的配对方案供选择</div>
          <div>• 平衡配对：基于历史记录生成更平衡的配对</div>
        </div>
      </div>
    </div>
  );
};

interface PairingPreviewProps {
  pairing: Pairing;
  showVs?: boolean;
}

const PairingPreview: React.FC<PairingPreviewProps> = ({ pairing, showVs = false }) => {
  return (
    <div className={`flex items-center justify-center ${showVs ? 'space-x-8' : 'space-x-4'}`}>
      {/* 队伍1 */}
      <div className="text-center">
        <div className={`bg-blue-100 border-2 border-blue-300 rounded-lg p-4 ${showVs ? 'min-w-[120px]' : ''}`}>
          <div className="text-blue-800 font-semibold mb-2">开室</div>
          <div className="space-y-1">
            {pairing.team1.map((player) => (
              <div key={player.id} className="text-sm">
                <div className="font-medium">{player.name}</div>
                <div className="text-xs text-blue-600">#{player.number}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* VS 标识 */}
      {showVs && (
        <div className="text-2xl font-bold text-gray-400">
          🤝
        </div>
      )}

      {/* 队伍2 */}
      <div className="text-center">
        <div className={`bg-green-100 border-2 border-green-300 rounded-lg p-4 ${showVs ? 'min-w-[120px]' : ''}`}>
          <div className="text-green-800 font-semibold mb-2">闭室</div>
          <div className="space-y-1">
            {pairing.team2.map((player) => (
              <div key={player.id} className="text-sm">
                <div className="font-medium">{player.name}</div>
                <div className="text-xs text-green-600">#{player.number}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PairingDisplay;